import clsx from "clsx";
import InputLabel from "components/InputLabel";
import useInvalidInput from "hooks/useInvalidInput";
import i18n from "languages/i18n";
import React, { useEffect, useState } from "react";

export interface SelectOption {
  id?: string | number;
  name: string;
  name_de?: string;
  name_en?: string;
  value?: string;
  disable?: boolean | null;
}
interface IProps {
  id?: string;
  className?: string;
  containerClassName?: string;
  labelName?: string;
  labelClassName?: string;
  options: SelectOption[];
  multiple?: boolean;
  required?: boolean;
  enforceRequired?: boolean;
  clearOption?: boolean;
  current?: number | string;
  size?: "large" | "middle" | "small";
  name?: string;
  onChange?: (value: string | undefined, name?: string) => void;
  type?: "primary" | "default";
  style?: React.CSSProperties;
  noMargin?: boolean;
  placeholder?: string;
  onBlur?: (name: string) => void;
  disable?: boolean;
  showMandatoryAlert?: boolean;
  errorMessage?: string;
  testID?: string;
  displayKey?: keyof SelectOption;
  helper?: React.ReactNode;
}

const SimpleSelect = React.forwardRef(
  (
    {
      id,
      className,
      containerClassName,
      labelName,
      labelClassName,
      options,
      current,
      multiple,
      required,
      enforceRequired,
      clearOption,
      size = "middle",
      style,
      name = "",
      onChange,
      type = "primary",
      noMargin,
      placeholder,
      onBlur,
      disable,
      showMandatoryAlert,
      errorMessage,
      testID,
      displayKey,
      ...props
    }: IProps,
    ref: React.Ref<HTMLSelectElement>
  ) => {
    const [selected, setSelected] = useState<number | string | undefined>(
      current || placeholder
    );
    const [showError, setShowError] = React.useState(false);

    const handleOnChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
      const currentValue = event.target.value;
      setSelected(currentValue);
      onChange && onChange(currentValue, event.target.name);
    };

    const selectRef = React.useRef<HTMLSelectElement>(null);

    useInvalidInput(selectRef, (isInvalid) => {
      if (isInvalid) {
        setShowError(true);
      } else {
        setShowError(false);
      }
    });

    useEffect(() => {
      setSelected(current || placeholder);
    }, [current]);

    const hasValidationError =
      required && selected === placeholder && showMandatoryAlert;

    return (
      <div
        style={style}
        className={clsx(`${containerClassName ?? ""}`, { "mb-4": !noMargin })}
      >
        {labelName && (
          <InputLabel
            labelName={labelName}
            name={id as string}
            required={required}
            className={labelClassName}
          />
        )}
        <div className="mx-auto">
          <select
            name={name}
            ref={ref ?? selectRef}
            data-invalid="false"
            onInvalid={() => setShowError(true)}
            required={required && enforceRequired !== false}
            multiple={multiple}
            className={clsx(
              "block w-full py-1 pl-2 pr-10 text-sm border-gray-300 truncate",
              "rounded-md focus:outline-none focus:border-indigo-500",
              {
                "h-large": size === "large",
                "h-middle": size === "middle",
                "h-small": size === "small",
                "border-black	bg-transparent": type === "default",
              },
              className,
              {
                "text-gray-500 font-normal text-sm":
                  selected === placeholder || disable,
                "!text-red-500": selected === placeholder && hasValidationError,
                "!border-red-500 placeholder:text-red-500": hasValidationError,
                "cursor-pointer": !disable,
              }
            )}
            value={selected}
            onChange={handleOnChange}
            onBlur={(e: React.FocusEvent<HTMLSelectElement>) => {
              if (required) {
                setShowError(e.currentTarget.value.length === 0);
              }
              onBlur && onBlur(name);
            }}
            disabled={disable}
            test-id={testID}
          >
            {placeholder && <option disabled>{placeholder}</option>}
            {!required && clearOption && <option value={""}>-</option>}
            {options.map((rec, index) => (
              <option
                key={index}
                value={rec.id}
                disabled={Boolean(rec.disable)}
              >
                {displayKey
                  ? rec[displayKey]
                  : i18n.language === "de"
                  ? rec.name_de ?? rec.name
                  : rec.name_en ?? rec.name}
              </option>
            ))}
          </select>
        </div>
        {errorMessage && hasValidationError && (
          <p className="text-red-600 text-sm font-normal mt-2">
            {errorMessage}
          </p>
        )}
        {props.helper && (
          <div
            className={clsx(
              "text-sm leading-5 font-normal mt-2 text-gray-500",
              { "text-red-600": hasValidationError }
            )}
          >
            {props.helper}
          </div>
        )}
      </div>
    );
  }
);

SimpleSelect.displayName = "SimpleSelect";

export default SimpleSelect;
